import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { NavGroup } from '@app/modules/nav/models/interfaces/nav-group.interface';
import { NavStateService } from '@app/modules/nav/services/nav-state.service';

/**
 * This service holds the active state of the vertical navigation menu.
 * It keeps track of what navigation group is selected and any sub-navigation items selected.
 * We use this information to style the active navigation items as selected, unselected, and partially selected.
 */
@Injectable({
    providedIn: 'root'
})
export class ActiveNavStateService {
    private _activeNavGroupId: string;
    private _activeSubNavId: string;

    get activeNavGroupId(): string {
        return this._activeNavGroupId;
    }

    get activeSubNavId(): string {
        return this._activeSubNavId;
    }

    constructor(
        private readonly navState: NavStateService,
        private readonly router: Router
    ) { }

    isNavGroupSelected(id: string): boolean {
        return this._activeNavGroupId === id;
    }

    isSubNavSelected(id: string): boolean {
        return this._activeSubNavId === id;
    }

    isSubNavUnset(): boolean {
        return this._activeSubNavId === undefined;
    }

    setActiveNavs(navGroupId?: string, subNavId?: string): void {
        this._activeNavGroupId = navGroupId
        this._activeSubNavId = subNavId;
    }

    clearActiveNavs(): void {
        this._activeNavGroupId = undefined;
        this._activeSubNavId = undefined;
    }

    /**
     * This method loops through the nav group structure to find and set the active routes.
     * The active routes are set in the nav
     * @private
     */
    public setActiveNav(): void {
        if (!this.navState.navGroups?.length) {
            return;
        }

        const { navGroupId, subNavId } = this.findActiveRouteIds(this.navState.navGroups);
        this.setActiveNavs(navGroupId, subNavId);
    }


    /**
     * This method takes an existing parent / child structure of our navigation groups,
     * Loops through them and finds which of their routes is active.
     *
     * Note: external links cannot be selected, nor can items that have a null link.
     *
     * If a parent nav group is selected, the navGroupId will be set, and the subNavId will be undefined.
     * If a child sub nav is selected, both the navGroupId and subNavId will be set
     * If none of the routes are set, both ids will be undefined.
     * @param navGroups
     */
    public findActiveRouteIds(navGroups: NavGroup[]): { navGroupId?: string; subNavId?: string; } {
        for (const navGroup of navGroups) {
            // check if parent nav is selected
            if (!navGroup.isExternalLink && navGroup.link !== null &&  this.isRouteActive(navGroup.link)) {
                return {
                    navGroupId: navGroup.id
                };
            }

            if (!navGroup.children?.length) {
                continue;
            }

            // check if child nav is selected
            for (const subNav of navGroup.children) {
                if (!subNav.isExternalLink && subNav.link !== null && subNav.link !== undefined && this.isRouteActive(subNav.link)) {
                    return {
                        navGroupId: navGroup.id,
                        subNavId: subNav.id
                    };
                }
            }
        }

        return {};
    }

    /**
     * Given an array of routes, this method determines if at least one route is active.
     * @param routes
     * @param exact
     */
    public areAnyRoutesActive(routes: string[][], exact: boolean = false): boolean {
        for (const route of routes) {
            if (this.isRouteActive(route, exact)) {
                return true;
            }
        }

        return false;
    }

    /**
     * This method checks to see if a specific url route is active.
     * This ignores query params, fragments, and matrix params.
     * @param route
     * @param exact - determines if the path params should use the exact path or a subset of the path
     */
    public isRouteActive(route: string[], exact: boolean = false): boolean {
        if (!this.router || !route) {
            return false;
        }

        const tree: UrlTree = this.router.createUrlTree(route);
        return this.router.isActive(
            tree,
            {
                paths: exact ? 'exact' : 'subset',
                queryParams: 'ignored',
                fragment: 'ignored',
                matrixParams: 'ignored'
            }
        );
    }
}
